import { Modal, SelectOptionType } from '@octano/global-ui';
import { useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { UpdateSessionFormFields } from '../types';
import { UpdateSessionForm } from './UpdateSessionForm';
import { StudentRiskEvaluationSessionEnrollment } from '../../../types';
import { StudentsRiskHistoryDetails } from '../../../parts/StudentRiskHistory/types';

type UpdateSessionModalProps = {
  sessionEnrollment?: StudentRiskEvaluationSessionEnrollment;
  options?: StudentsRiskHistoryDetails['formOptions'];
  isReadOnly?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export function UpdateSessionModal({
  isReadOnly = false,
  onClose,
  onSuccess,
  sessionEnrollment,
  options,
}: UpdateSessionModalProps) {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_HISTORY,
  );

  const texts = useMemo(() => {
    return {
      title: t('studentHistory.updateSessionModal.title'),
      cancel: t('studentHistory.updateSessionModal.cancel'),
      save: t('studentHistory.updateSessionModal.save'),
    };
  }, [t]);

  const getSelectedOption = (
    options: SelectOptionType[],
    value: string | boolean | number | null,
  ) => {
    if (value === null) {
      return null;
    }

    if (typeof value === 'boolean') {
      return options.find(
        (option) =>
          option.value === (value ? 1 : 0) ||
          option.value === ((value ? true : false) as never),
      );
    }
    return options.find((option) => option.value === value);
  };

  const methods = useForm<UpdateSessionFormFields>({
    defaultValues: {
      date: sessionEnrollment?.definedAt,
      observation: sessionEnrollment?.notes || null,
      situation: getSelectedOption(
        options?.stateOptions || [],
        sessionEnrollment?.state || null,
      ),
      continuesInCEIA: getSelectedOption(
        options?.keepForNextSessionOptions || [],
        !!sessionEnrollment?.keepForNextSession,
      ),
      conditionality: getSelectedOption(
        options?.conditionalityOptions || [],
        !!sessionEnrollment?.conditionality,
      ),
      riskLevel: getSelectedOption(
        options?.riskLevelOptions || [],
        sessionEnrollment?.calculatedRiskLevel || null,
      ),
    },
  });

  return (
    <Modal isOpen toggle={onClose} size="xl" closeOnBackdrop={false}>
      <Row className="text-center">
        <Col xs={12} className="pb-3">
          <span className="fs-22 text-dark">{texts.title}</span>
        </Col>
      </Row>
      <Row xs={12}>
        <FormProvider {...methods}>
          <UpdateSessionForm
            onClose={onClose}
            riskLevelOptions={options?.riskLevelOptions || []}
            keepForNextSessionOptions={options?.keepForNextSessionOptions || []}
            conditionalityOptions={options?.conditionalityOptions || []}
            situationOptions={options?.stateOptions || []}
            onSuccess={onSuccess}
            sessionEnrollment={sessionEnrollment}
            isReadOnly={isReadOnly}
          />
        </FormProvider>
      </Row>
    </Modal>
  );
}
